import { useState } from "react";
import SearchBar from "@/components/common/SearchBar";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { ChevronDown, ChevronRight, Flag, ClipboardList } from "lucide-react";
import { InspectionReportInline } from "@/components/InspectionReportInline";
import { getInspection } from "@/lib/api";

// Inspections tab on the customer side-sheet. Two stacked lists:
//   • Completed Inspections — expandable row → fetches the full
//     report on demand via `getInspection(id)` then renders
//     <InspectionReportInline>. Color-coded pass-rate pill and
//     flagged-item count.
//   • In-Progress Inspections — quick "Continue" button that opens
//     /inspections/:id in a new tab.
// Extracted from Clients.jsx (Feb 2026, iter180).
export function ClientInspectionsTab({
  clientInspections,
  expandedInspId,
  setExpandedInspId,
  expandedInspData,
  setExpandedInspData,
}) {
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const hit = (i) => !q || [i.template_name, i.wo_number, i.site_name, i.inspector_name, i.scope_name].some((v) => (v || "").toLowerCase().includes(q));
  const completed = clientInspections.filter((i) => i.status === "complete" && hit(i));
  const inProgress = clientInspections.filter((i) => i.status !== "complete" && hit(i));

  return (
    <TabsContent value="inspections" className="mt-4">
      <div className="space-y-3" data-testid="client-inspections-tab">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBar value={search} onChange={setSearch} placeholder="Search inspections..." testId="search-inspections" />
        </div>
        <h4 className="text-sm font-semibold text-slate-700">Completed Inspections</h4>
        {completed.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm border rounded-lg border-dashed">
            <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No completed inspections for this customer</p>
            <p className="text-xs mt-1">Inspections are created from within Work Orders</p>
          </div>
        ) : (
          <div className="space-y-2">
            {completed
              .sort((a, b) => (b.conducted_date || "").localeCompare(a.conducted_date || ""))
              .map((insp) => {
                let total = 0;
                let flagged = 0;
                (insp.locations || []).forEach((l) =>
                  (l.responses || []).forEach((r) => {
                    total++;
                    if (r.flagged) flagged++;
                  })
                );
                const passRate = total ? Math.round(((total - flagged) / total) * 100) : 0;
                const isExpanded = expandedInspId === insp.id;
                return (
                  <div key={insp.id} className="border rounded-lg overflow-hidden" data-testid={`client-insp-${insp.id}`}>
                    <button
                      className="w-full flex items-center justify-between p-3 hover:bg-slate-50 text-left"
                      onClick={async () => {
                        if (isExpanded) {
                          setExpandedInspId(null);
                          setExpandedInspData(null);
                          return;
                        }
                        setExpandedInspId(insp.id);
                        try {
                          const res = await getInspection(insp.id);
                          setExpandedInspData(res.data);
                        } catch {
                          setExpandedInspData(insp);
                        }
                      }}
                      data-testid={`toggle-insp-${insp.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold text-xs text-slate-800">{insp.template_name}</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {insp.site_name || "—"} &middot; {insp.conducted_date} &middot; {insp.inspector_name}
                            {(insp.locations || []).length > 0 && (
                              <span className="text-slate-500">
                                {" "}
                                &middot;{" "}
                                {(insp.locations || [])
                                  .map((l) => (l.identifier ? `${l.name} (${l.identifier})` : l.name))
                                  .join(", ")}
                              </span>
                            )}
                            {insp.wo_number && <span className="text-blue-500 ml-1">WO# {insp.wo_number}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {flagged > 0 && (
                          <span className="text-xs text-red-500 flex items-center gap-1">
                            <Flag className="w-3 h-3" />
                            {flagged}
                          </span>
                        )}
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            passRate >= 90
                              ? "bg-emerald-100 text-emerald-700"
                              : passRate >= 70
                                ? "bg-amber-100 text-amber-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {passRate}%
                        </span>
                      </div>
                    </button>
                    {isExpanded && expandedInspData && <InspectionReportInline data={expandedInspData} />}
                  </div>
                );
              })}
          </div>
        )}

        {inProgress.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">In Progress</h4>
            <div className="space-y-2">
              {inProgress.map((insp) => (
                <div
                  key={insp.id}
                  className="border rounded-lg p-3 bg-amber-50/30 flex items-center justify-between"
                  data-testid={`client-insp-progress-${insp.id}`}
                >
                  <div>
                    <span className="font-medium text-xs">{insp.template_name}</span>
                    <span className="text-xs text-slate-400 ml-2">
                      {insp.conducted_date} &middot; {insp.inspector_name}
                      {(insp.locations || []).length > 0 &&
                        ` · ${(insp.locations || [])
                          .map((l) => (l.identifier ? `${l.name} (${l.identifier})` : l.name))
                          .join(", ")}`}
                    </span>
                    {insp.wo_number && <span className="text-xs text-blue-500 ml-2">WO# {insp.wo_number}</span>}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-amber-600"
                    onClick={() => window.open(`/inspections/${insp.id}`, "_blank")}
                  >
                    Continue
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </TabsContent>
  );
}
